import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  MenuItem,
  InputAdornment,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  useTheme
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff,
  ExpandMore as ExpandMoreIcon,
  Menu as MenuIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { organizationApi, CreateOrganizationWithUserRQ } from '../../utils/organizationApi';
import { formatPhoneNumber } from '../../utils/phoneMask';
import { useAuth } from '../../contexts/AuthContext';

interface FormErrors {
  organizationName?: string;
  name?: string;
  email?: string;
  phone?: string;
  password?: string;
  confirmPassword?: string;
  initialCredits?: string;
}

const CreateOrganizationWithUserForm: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [form, setForm] = useState<CreateOrganizationWithUserRQ>({
    organizationName: '',
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    initialCredits: 10000,
    permissions: {
      menuPermissions: {
        canViewDashboard: true,
        canViewLeads: true,
        canViewConversations: true,
        canViewReports: false,
        canViewAgents: true,
        canViewTemplates: false,
        canViewCredits: true,
        canViewPlans: false,
        canViewUsers: true,
        canViewSegments: true,
        canViewSettings: true,
        canViewFinanceiro: false,
      },
      alertPermissions: {
        showTrialAlerts: false,
        showPaymentAlerts: false,
        showWelcomeModals: false,
        showPlanInactiveAlerts: false,
      },
      canCreateAgents: false,
    }
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!form.organizationName.trim()) {
      newErrors.organizationName = 'Nome da organização é obrigatório';
    }

    if (!form.name.trim()) {
      newErrors.name = 'Nome do usuário é obrigatório';
    }

    if (!form.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Email deve ter um formato válido';
    }

    if (!form.phone.trim()) {
      newErrors.phone = 'Telefone é obrigatório';
    }

    if (!form.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (form.password.length < 6) {
      newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
    }

    if (!form.confirmPassword) {
      newErrors.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = 'Senhas não coincidem';
    }

    // Validação de créditos será feita pelo backend

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const canSubmit = useMemo(() => {
    return form.organizationName &&
      form.name &&
      form.email &&
      form.phone &&
      form.password &&
      form.confirmPassword &&
      form.password === form.confirmPassword;
  }, [form]);

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await organizationApi.createWithUser(form);
      toast.success('Organização e usuário criados com sucesso!');
      navigate('..');
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao criar organização');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = formatPhoneNumber(e.target.value);
    setForm({ ...form, phone: masked });
  };

  const updatePermissions = (path: string, value: boolean) => {
    setForm(prev => {
      const newPermissions = { ...prev.permissions };
      const pathParts = path.split('.');
      let current: any = newPermissions;

      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]];
      }

      current[pathParts[pathParts.length - 1]] = value;

      return { ...prev, permissions: newPermissions };
    });
  };

  const getMenuLabel = (key: string): string => {
    const labels: Record<string, string> = {
      canViewDashboard: 'Dashboard',
      canViewLeads: 'Leads',
      canViewConversations: 'Conversas',
      canViewReports: 'Relatórios',
      canViewAgents: 'Agentes',
      canViewTemplates: 'Templates',
      canViewCredits: 'Créditos',
      canViewPlans: 'Planos',
      canViewUsers: 'Usuários',
      canViewSegments: 'Segmentos',
      canViewSettings: 'Configurações',
      canViewFinanceiro: 'Financeiro',
    };
    return labels[key] || key;
  };

  const getAlertLabel = (key: string): string => {
    const labels: Record<string, string> = {
      showTrialAlerts: 'Alertas de período de teste',
      showPaymentAlerts: 'Alertas de pagamento',
      showWelcomeModals: 'Modais de boas-vindas',
      showPlanInactiveAlerts: 'Alertas de plano inativo',
    };
    return labels[key] || key;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('..')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Nova Organização
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon />
          Informações da Organização
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Nome da Organização"
            value={form.organizationName}
            onChange={e => setForm({ ...form, organizationName: e.target.value })}
            error={!!errors.organizationName}
            helperText={errors.organizationName}
            required
          />
          <TextField
            fullWidth
            type="number"
            label="Créditos Iniciais"
            value={form.initialCredits}
            onChange={e => setForm({ ...form, initialCredits: parseInt(e.target.value) || 10000 })}
            helperText={
              user?.isWhiteLabel && user.whiteLabelCreditInfo
                ? `Quantidade de créditos que a organização terá inicialmente. Disponível: ${user.whiteLabelCreditInfo.availableCredits.toLocaleString()}/${user.whiteLabelCreditInfo.maxCredits.toLocaleString()}`
                : "Quantidade de créditos que a organização terá inicialmente"
            }
            error={!!errors.initialCredits}
            FormHelperTextProps={{
              error: !!errors.initialCredits
            }}
          />
          {errors.initialCredits && (
            <FormHelperText error>
              {errors.initialCredits}
            </FormHelperText>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          Informações do Primeiro Usuário
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Nome Completo"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name}
            required
          />

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
            <TextField
              fullWidth
              label="Telefone"
              value={form.phone}
              onChange={handlePhoneChange}
              error={!!errors.phone}
              helperText={errors.phone}
              placeholder="(00) 00000-0000"
              required
            />
          </Box>

          <TextField
            fullWidth
            label="Função"
            value="Administrador"
            disabled
            helperText="O primeiro usuário sempre será criado como Administrador da organização"
          />

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <TextField
              fullWidth
              label="Senha"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              error={!!errors.password}
              helperText={errors.password}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirmar Senha"
              type={showConfirmPassword ? 'text' : 'password'}
              value={form.confirmPassword}
              onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Permissões de Menu - Oculto para usuários whitelabel */}
      {!user?.isWhiteLabel && (
        <Accordion defaultExpanded sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MenuIcon />
              <Typography variant="h6">Permissões de Menu</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {Object.entries(form.permissions.menuPermissions).map(([key, value]) => (
                <Box key={key} sx={{ minWidth: { xs: '100%', sm: '45%', md: '30%' } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={value}
                        onChange={(_, checked) => updatePermissions(`menuPermissions.${key}`, checked)}
                      />
                    }
                    label={getMenuLabel(key)}
                  />
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Permissões de Alertas - Oculto para usuários whitelabel */}
      {!user?.isWhiteLabel && (
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsIcon />
              <Typography variant="h6">Permissões de Alertas</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {Object.entries(form.permissions.alertPermissions).map(([key, value]) => (
                <Box key={key} sx={{ minWidth: { xs: '100%', sm: '45%' } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={value}
                        onChange={(_, checked) => updatePermissions(`alertPermissions.${key}`, checked)}
                      />
                    }
                    label={getAlertLabel(key)}
                  />
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Outras Permissões */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            <Typography variant="h6">Outras Permissões</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Switch
                checked={form.permissions.canCreateAgents}
                onChange={(_, checked) => updatePermissions('canCreateAgents', checked)}
              />
            }
            label="Pode criar novos agentes"
          />
        </AccordionDetails>
      </Accordion>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={() => navigate('..')}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
          disabled={!canSubmit || loading}
          onClick={handleSubmit}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Criando...' : 'Criar Organização'}
        </Button>
      </Box>
    </Paper>
  );
};

export default CreateOrganizationWithUserForm;
